/**
 * Loads image/video documents (referenced by `refImage` / `refVideo`) in batches.
 *
 * Every <Picture> and <Video> used to fire its own Sanity API request on mount,
 * which resulted in 40+ requests per page view (with duplicates for the same image).
 * Instead, all ids requested within a short window are collected and resolved with
 * a single query. Results are cached for the lifetime of the app, so client-side
 * navigation does not refetch media that was already loaded (e.g. footer images).
 *
 * `primeMedia` resolves all media referenced in already fetched content up front. On the
 * server this means images and videos are part of the rendered HTML and the payload, so the
 * browser does not have to send any media request after hydration.
 */

export interface SanityMediaDocument {
  _id: string;
  asset: {
    _id: string;
    url: string;
    mimeType?: string;
    metadata?: {
      dimensions?: { width: number; height: number; aspectRatio: number };
    };
  } | null;
}

// Keep the GET url below the Sanity client limit (~11kB), otherwise it switches
// to POST which bypasses the API CDN.
const MAX_IDS_PER_REQUEST = 40;
const BATCH_WINDOW_MS = 16;

const mediaQuery = groq`*[_id in $ids]{
  _id,
  "asset": upload.asset->{
    _id,
    url,
    mimeType,
    metadata { dimensions }
  }
}`;

type MediaState = Record<string, SanityMediaDocument | null>;

const MEDIA_KEYS = ['refImage', 'refVideo'];

// collects the ids of all image/video documents referenced somewhere in the given content
const collectMediaIds = (node: unknown, ids: Set<string>) => {
  if (Array.isArray(node)) {
    node.forEach((child) => collectMediaIds(child, ids));
  } else if (node && typeof node === 'object') {
    for (const [key, value] of Object.entries(node)) {
      const ref = (value as { _ref?: unknown } | null)?._ref;
      if (MEDIA_KEYS.includes(key) && typeof ref === 'string') {
        ids.add(ref);
      } else {
        collectMediaIds(value, ids);
      }
    }
  }
};

const cache = new Map<string, Promise<SanityMediaDocument | null>>();
let pending = new Map<string, (doc: SanityMediaDocument | null) => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

export const useSanityMediaLoader = () => {
  const sanity = useSanity();
  // shared between server render and browser through the Nuxt payload
  const state = useState<MediaState>('sanity-media', () => ({}));

  const fetchChunk = (ids: string[]) =>
    cachedSanityFetch<SanityMediaDocument[]>(sanity, mediaQuery, { ids }, TTL.long);

  /** Synchronous lookup: `undefined` means "not loaded yet", `null` means "does not exist". */
  const getMedia = (id: string): SanityMediaDocument | null | undefined => state.value[id];

  /** Loads all media referenced in the given content with as few requests as possible. */
  const primeMedia = async (...sources: unknown[]) => {
    const found = new Set<string>();
    sources.forEach((source) => collectMediaIds(source, found));
    const ids = [...found].filter((id) => !(id in state.value)).sort();

    for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      const chunk = ids.slice(i, i + MAX_IDS_PER_REQUEST);
      try {
        const docs = await fetchChunk(chunk);
        const byId = new Map(docs.map((doc) => [doc._id, doc]));
        chunk.forEach((id) => {
          state.value[id] = byId.get(id) ?? null;
        });
      } catch (error) {
        // not fatal, <Picture> and <Video> fall back to loading on demand in the browser
        console.error(error);
      }
    }
  };

  const flush = async () => {
    timer = null;
    const batch = pending;
    pending = new Map();

    // Sorted ids give a stable url, so the same page hits the API CDN cache.
    const ids = [...batch.keys()].sort();

    for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      const chunk = ids.slice(i, i + MAX_IDS_PER_REQUEST);

      try {
        const docs = await fetchChunk(chunk);
        const byId = new Map(docs.map((doc) => [doc._id, doc]));
        chunk.forEach((id) => {
          const doc = byId.get(id) ?? null;
          state.value[id] = doc;
          batch.get(id)?.(doc);
        });
      } catch (error) {
        console.error(error);
        chunk.forEach((id) => {
          // allow a retry on the next request
          cache.delete(id);
          batch.get(id)?.(null);
        });
      }
    }
  };

  const loadMedia = (id: string): Promise<SanityMediaDocument | null> => {
    if (!id) {
      return Promise.resolve(null);
    }

    if (id in state.value) {
      return Promise.resolve(state.value[id]);
    }

    const cached = cache.get(id);
    if (cached) {
      return cached;
    }

    const promise = new Promise<SanityMediaDocument | null>((resolve) => {
      pending.set(id, resolve);
    });
    cache.set(id, promise);

    if (!timer) {
      timer = setTimeout(flush, BATCH_WINDOW_MS);
    }

    return promise;
  };

  return { loadMedia, getMedia, primeMedia };
};
